/** Used in SMS, WhatsApp, and Email template editors */
import { useEffect, useMemo, useState } from 'react';

import { FormField } from 'ui/formField';
import { MessageMedium } from 'constants/messageMediums';

import { useQueryParamContext } from '../context/QueryParamContext';
import { useEditableFieldsContext } from '../context/EditableFieldsContext';
import { useTemplateQueryContext } from '../context/TemplateQueryContext';
import { isMediumShortLinkCompatible } from './shortLinksHelpers';
import {
  CheckboxField,
  FieldExpanded,
  LinkContainer,
  LinkDescription,
  SettingsFieldMoreDetailInRow,
  SettingsReadOnlyText,
  TwoRowFieldDisplay,
} from '.';
import { getIsEditMode } from './utils';
import { GOOGLE_ANALYTICS_DISPLAY_CONTENT } from './constants';
import { InputPrefix, StyledFilterInput } from './styles';
import { LinksCompatibleMediumData } from './types';

export const GoogleAnalyticsFormSection = ({
  isLinkShorteningEnabled,
  isEditDisabled = false,
}: {
  isLinkShorteningEnabled: boolean;
  isEditDisabled?: boolean;
}) => {
  const { view } = useQueryParamContext();
  const { isEditMode, liveTemplate, setCurrentMediumField } = useEditableFieldsContext();
  const { medium } = useTemplateQueryContext();
  const { mediumData } = liveTemplate;

  const googleAnalyticsCampaignName = (mediumData as LinksCompatibleMediumData)
    ?.googleAnalyticsCampaignName;
  const [inputValue, setInputValue] = useState(
    googleAnalyticsCampaignName?.length
      ? googleAnalyticsCampaignName
      : GOOGLE_ANALYTICS_DISPLAY_CONTENT.inputDefaultValue,
  );
  const [isGoogleAnalyticsEnabled, setIsGoogleAnalyticsEnabled] = useState(
    !!googleAnalyticsCampaignName?.length,
  );

  /** Check if link shortening is enabled */
  const isFormFieldDisabled = isMediumShortLinkCompatible(medium as MessageMedium)
    ? !isLinkShorteningEnabled
    : false;
  const handleInputChange = (newInput: string) => {
    setInputValue(newInput);
    setCurrentMediumField('googleAnalyticsCampaignName', newInput);
  };

  const isEditModeVisible = getIsEditMode(view, isEditMode) && !isEditDisabled;

  const googleAnalyticsLink = useMemo(
    () =>
      `utm_source=Iterable&utm_medium=${medium.toLowerCase()}&utm_campaign=${encodeURI(
        inputValue,
      )}`,
    [inputValue],
  );

  const handleCheckboxClick = () => {
    /** Clear campaign name from live template when unchecking box */
    if (isGoogleAnalyticsEnabled) {
      setCurrentMediumField('googleAnalyticsCampaignName', null);
    } else {
      /** Sets default value on enablement */
      setCurrentMediumField(
        'googleAnalyticsCampaignName',
        GOOGLE_ANALYTICS_DISPLAY_CONTENT.inputDefaultValue,
      );
    }
    setIsGoogleAnalyticsEnabled(!isGoogleAnalyticsEnabled);
  };

  /**
   * Uncheck checkbox if Google analytics is empty upon leaving edit mode.
   * Check checkbox if Google analytics is not empty on edit mode.
   * */
  useEffect(() => {
    if (!isEditModeVisible && isGoogleAnalyticsEnabled) {
      if (!inputValue?.length) {
        setIsGoogleAnalyticsEnabled(false);
      }
    } else if (isEditModeVisible) {
      if (googleAnalyticsCampaignName?.length) {
        setIsGoogleAnalyticsEnabled(true);
      }
    }
  }, [isEditModeVisible]);

  // Updates when the user clicks cancel and a template is refetched
  useEffect(() => {
    if (isGoogleAnalyticsEnabled) {
      setIsGoogleAnalyticsEnabled(!!googleAnalyticsCampaignName);
      if (googleAnalyticsCampaignName) setInputValue(googleAnalyticsCampaignName);
    }
  }, [googleAnalyticsCampaignName]);

  useEffect(() => {
    if (isMediumShortLinkCompatible(medium as MessageMedium) && !isLinkShorteningEnabled) {
      setIsGoogleAnalyticsEnabled(false);
      setCurrentMediumField('googleAnalyticsCampaignName', null);
    }
  }, [isLinkShorteningEnabled]);

  return (
    <FormField
      data-test="google-analytics-form-section"
      isDisabled={isEditModeVisible && isFormFieldDisabled}
      isHorizontalLayout
      labelText={GOOGLE_ANALYTICS_DISPLAY_CONTENT.labelText}
    >
      {isEditModeVisible ? (
        <>
          <CheckboxField
            isDisabled={isFormFieldDisabled}
            label={GOOGLE_ANALYTICS_DISPLAY_CONTENT.checkboxLabel}
            onClick={handleCheckboxClick}
            status={isGoogleAnalyticsEnabled}
          />

          {isGoogleAnalyticsEnabled && (
            <>
              <FieldExpanded>
                <StyledFilterInput
                  id="google-analytics-input"
                  isDisabled={isFormFieldDisabled}
                  maxLength={255}
                  onChange={handleInputChange}
                  Prefix={<InputPrefix>{GOOGLE_ANALYTICS_DISPLAY_CONTENT.inputPrefix}</InputPrefix>}
                  value={inputValue}
                />
                <LinkDescription>{GOOGLE_ANALYTICS_DISPLAY_CONTENT.helpText}</LinkDescription>
                <LinkContainer>{googleAnalyticsLink}</LinkContainer>
              </FieldExpanded>
            </>
          )}
        </>
      ) : (
        <TwoRowFieldDisplay data-test="google-analytics-read-only-display">
          <SettingsReadOnlyText
            isUnset={!isGoogleAnalyticsEnabled}
            data-test="google-analytics-on-off"
          >
            {googleAnalyticsCampaignName?.length ? 'On' : 'Off'}
          </SettingsReadOnlyText>
          {googleAnalyticsCampaignName?.length && googleAnalyticsCampaignName && (
            <SettingsFieldMoreDetailInRow data-test="google-analytics-detail">
              {googleAnalyticsCampaignName}
            </SettingsFieldMoreDetailInRow>
          )}
        </TwoRowFieldDisplay>
      )}
    </FormField>
  );
};
